/**
 * Level Wave Definitions
 * Based on Level_Wave_Template.md - Simple Arcade Level Plan
 * 
 * 12 levels total, 4 regions, 3 levels per region
 */

export interface WaveSpawn {
  enemyType: 'greed' | 'hunger_bug' | 'shadow';
  count: number;
  spawnDelay: number; // seconds between spawns within wave
}

export interface WaveDefinition {
  duration: number; // seconds
  spawns: WaveSpawn[];
  powerUpDrop?: {
    type: string;
    afterEnemyCount: number; // drop after this many enemies killed
  };
  purpose: string; // design intent
}

export interface LevelDefinition {
  levelNumber: number;
  regionName: string;
  backgroundTheme: string;
  musicCue: string;
  durationTarget: number; // seconds
  chainSegments: number;
  chainHealth: number;
  waves: WaveDefinition[];
  playerFeelGoals: {
    early: string;
    mid: string;
    late: string;
    completion: string;
  };
}

// REGION: Heartland (Levels 1-3)
export const LEVEL_DEFINITIONS: Record<number, LevelDefinition> = {
  1: {
    levelNumber: 1,
    regionName: 'Heartland',
    backgroundTheme: 'Rural sunrise',
    musicCue: 'Dawn of Hope',
    durationTarget: 80,
    chainSegments: 6,
    chainHealth: 1,
    waves: [
      {
        duration: 30,
        spawns: [
          { enemyType: 'greed', count: 8, spawnDelay: 2.5 }
        ],
        purpose: 'Intro, pacing'
      },
      {
        duration: 35,
        spawns: [
          { enemyType: 'greed', count: 10, spawnDelay: 2 }
        ],
        powerUpDrop: {
          type: 'repair',
          afterEnemyCount: 5
        },
        purpose: 'Teach chain defense'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'greed', count: 8, spawnDelay: 2.5 },
          { enemyType: 'hunger_bug', count: 6, spawnDelay: 3 }
        ],
        purpose: 'Add second enemy type'
      },
      {
        duration: 35,
        spawns: [
          { enemyType: 'hunger_bug', count: 10, spawnDelay: 2 }
        ],
        purpose: 'Pattern practice'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'greed', count: 8, spawnDelay: 2 },
          { enemyType: 'hunger_bug', count: 8, spawnDelay: 2.5 }
        ],
        powerUpDrop: {
          type: 'shield',
          afterEnemyCount: 8
        },
        purpose: 'Power-up intro'
      }
    ],
    playerFeelGoals: {
      early: 'Calm, curious',
      mid: 'Slight tension, learning',
      late: 'Confident, ready for more',
      completion: 'Accomplished'
    }
  },
  
  2: {
    levelNumber: 2,
    regionName: 'Heartland',
    backgroundTheme: 'Rural sunrise',
    musicCue: 'Dawn of Hope',
    durationTarget: 90,
    chainSegments: 7,
    chainHealth: 1,
    waves: [
      {
        duration: 30,
        spawns: [
          { enemyType: 'greed', count: 12, spawnDelay: 1 }
        ],
        purpose: 'Warmup'
      },
      {
        duration: 35,
        spawns: [
          { enemyType: 'hunger_bug', count: 10, spawnDelay: 1.5 }
        ],
        powerUpDrop: {
          type: 'repair',
          afterEnemyCount: 5
        },
        purpose: 'Chain movement'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'greed', count: 10, spawnDelay: 1.2 },
          { enemyType: 'hunger_bug', count: 10, spawnDelay: 1.5 }
        ],
        purpose: 'Mixed threat'
      },
      {
        duration: 35,
        spawns: [
          { enemyType: 'greed', count: 15, spawnDelay: 0.8 }
        ],
        powerUpDrop: {
          type: 'neon_shield',
          afterEnemyCount: 8
        },
        purpose: 'Power-up test'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'hunger_bug', count: 12, spawnDelay: 1.2 }
        ],
        purpose: 'Endurance'
      }
    ],
    playerFeelGoals: {
      early: 'Confident',
      mid: 'Alert',
      late: 'Triumphant',
      completion: 'Satisfied'
    }
  },
  
  3: {
    levelNumber: 3,
    regionName: 'Heartland',
    backgroundTheme: 'Rural sunrise',
    musicCue: 'Dawn of Hope',
    durationTarget: 95,
    chainSegments: 7,
    chainHealth: 1,
    waves: [
      {
        duration: 35,
        spawns: [
          { enemyType: 'greed', count: 15, spawnDelay: 0.9 }
        ],
        purpose: 'Quick start'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'greed', count: 12, spawnDelay: 1 },
          { enemyType: 'hunger_bug', count: 12, spawnDelay: 1.2 }
        ],
        powerUpDrop: {
          type: 'repair',
          afterEnemyCount: 10
        },
        purpose: 'Mixed pressure'
      },
      {
        duration: 40,
        spawns: [
          { enemyType: 'hunger_bug', count: 18, spawnDelay: 1 }
        ],
        purpose: 'Hunger focus'
      },
      {
        duration: 35,
        spawns: [
          { enemyType: 'greed', count: 20, spawnDelay: 0.7 }
        ],
        powerUpDrop: {
          type: 'shield',
          afterEnemyCount: 12
        },
        purpose: 'Speed test'
      },
      {
        duration: 45,
        spawns: [
          { enemyType: 'greed', count: 15, spawnDelay: 0.8 },
          { enemyType: 'hunger_bug', count: 15, spawnDelay: 1 }
        ],
        purpose: 'Final challenge'
      }
    ],
    playerFeelGoals: {
      early: 'Ready',
      mid: 'Challenged',
      late: 'Focused',
      completion: 'Victorious'
    }
  }
};

// Helper to get level definition
export function getLevelDefinition(levelNumber: number): LevelDefinition | null {
  return LEVEL_DEFINITIONS[levelNumber] || null;
}

// Helper to get total waves for a level
export function getTotalWaves(levelNumber: number): number {
  const level = getLevelDefinition(levelNumber);
  return level ? level.waves.length : 5; // default to 5 if not found
}
